<template>
  <div class="calendar-view">
    <f7-block class="cal-nav">
      <f7-link icon-f7="chevron_left" @click="prevMonth"></f7-link>
      <div class="cal-month">{{ monthLabel }}</div>
      <f7-link icon-f7="chevron_right" @click="nextMonth"></f7-link>
      <f7-link class="cal-today" @click="goToday">{{
        $t("calendar.today")
      }}</f7-link>
    </f7-block>

    <f7-block class="cal-grid-block">
      <div class="cal-weekdays">
        <div v-for="d in weekdays" :key="d" class="cal-weekday">{{ d }}</div>
      </div>
      <div class="cal-grid">
        <div
          v-for="day in gridDays"
          :key="day.valueOf()"
          class="cal-cell"
          :class="{
            'cal-cell--other': !isSameMonth(day),
            'cal-cell--today': isToday(day),
            'cal-cell--selected': isSelected(day),
          }"
          @click="selectDay(day)"
        >
          <span class="cal-day-num">{{ day.date() }}</span>
          <span v-if="hasEvents(day)" class="cal-dot"></span>
        </div>
      </div>
    </f7-block>

    <f7-block-title>{{
      $t("calendar.eventsOf", { date: selectedLabel })
    }}</f7-block-title>
    <f7-block v-if="loading" class="loading-block">
      <f7-preloader color="green" />
    </f7-block>
    <template v-else>
      <f7-card
        v-for="event in selectedEvents"
        :key="event.id"
        class="cal-event-card"
      >
        <f7-card-header>
          <div
            class="display-flex justify-content-space-between align-items-center"
          >
            <div>
              <div class="text-weight-bold">{{ event.title }}</div>
              <div class="text-subtitle">
                {{ formatDateTime(event.startAt, event.allDay) }}
              </div>
            </div>
            <div class="text-align-right">
              <div
                class="display-flex align-items-center gap-small justify-content-end"
              >
                <f7-badge :color="badgeColor(event.type)">{{
                  typeLabel(event.type)
                }}</f7-badge>
                <f7-icon
                  v-if="event.pinned"
                  f7="pin"
                  size="12"
                  color="orange"
                />
              </div>
              <div class="text-xs margin-top-half">
                <span class="text-green">{{
                  $t("calendar.goingCount", {
                    count: event.summary?.goingCount ?? 0,
                  })
                }}</span
                >,
                <span class="text-orange">{{
                  $t("calendar.maybeCount", {
                    count: event.summary?.maybeCount ?? 0,
                  })
                }}</span
                >,
                <span class="text-red">{{
                  $t("calendar.declinedCount", {
                    count: event.summary?.declinedCount ?? 0,
                  })
                }}</span>
              </div>
            </div>
          </div>
        </f7-card-header>
        <f7-card-content>
          <div class="text-subtitle">{{ event.description }}</div>
          <div v-if="event.location" class="text-xs text-gray margin-top-half">
            <f7-icon f7="map" size="16"></f7-icon> {{ event.location }}
          </div>
        </f7-card-content>
        <f7-card-footer>
          <div
            class="display-flex justify-content-space-between align-items-center cal-footer"
          >
            <div class="display-flex gap-small">
              <f7-button
                small
                round raised
                :outline="myStatus(event) !== 'GOING'"
                :fill="myStatus(event) === 'GOING'"
                color="green"
                @click="setAttendance(event, 'GOING')"
              >
                {{ $t("calendar.attending") }}
              </f7-button>
              <f7-button
                small
                round raised
                :outline="myStatus(event) !== 'MAYBE'"
                :fill="myStatus(event) === 'MAYBE'"
                color="orange"
                @click="setAttendance(event, 'MAYBE')"
              >
                {{ $t("calendar.maybeAttend") }}
              </f7-button>
              <f7-button
                small
                round raised
                :outline="myStatus(event) !== 'DECLINED'"
                :fill="myStatus(event) === 'DECLINED'"
                color="red"
                @click="setAttendance(event, 'DECLINED')"
              >
                {{ $t("calendar.notAttending") }}
              </f7-button>
            </div>
            <div class="display-flex gap-small">
              <f7-button small outline color="blue" @click="downloadIcs(event)">
                {{ $t("calendar.addToCalendar") }}
              </f7-button>
              <f7-button
                small
                fill
                round raised
                color="blue"
                @click="openEventDetails(event)"
              >
                {{ $t("calendar.details") }}
              </f7-button>
            </div>
          </div>
        </f7-card-footer>
      </f7-card>

      <f7-block v-if="!selectedEvents.length" class="empty-text">
        {{ $t("calendar.noEvents") }}
      </f7-block>
    </template>

    <f7-fab
      v-if="isAdmin"
      position="right-bottom"
      class="cal-fab"
      color="green"
      @click="openCreateEvent"
    >
      <f7-icon f7="plus"></f7-icon>
    </f7-fab>

    <AdaptiveSheet class="cal-event-modal" :close-by-backdrop-click="false">
      <f7-page>
        <f7-navbar
          :title="
            editingEvent
              ? $t('calendar.editEventTitle')
              : $t('calendar.newEventTitle')
          "
        >
          <f7-nav-right>
            <f7-link sheet-close popup-close
              ><i class="icon icon-close"></i
            ></f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-block>
          <f7-list form dividers>
            <f7-list-input
              v-model:value="form.title"
              type="text"
              :label="$t('calendar.title')"
              :placeholder="$t('calendar.titlePlaceholder')"
              required
            ></f7-list-input>

            <f7-list-input
             v-model:value="form.description"
              type="textarea"
              :label="$t('calendar.description')"
              :placeholder="$t('calendar.descriptionPlaceholder')"
            ></f7-list-input>

            <f7-list-input
              v-model:value="form.startAt"
              type="datetime-local"
              :label="$t('calendar.start')"
              required
            ></f7-list-input>

            <f7-list-input
             v-model:value="form.endAt"
              type="datetime-local"
              :label="$t('calendar.end')"
            ></f7-list-input>

            <f7-list-input
              v-model:value="form.location"
              type="text"
              :label="$t('calendar.location')"
              :placeholder="$t('calendar.locationPlaceholder')"
            ></f7-list-input>
            <f7-list-input
              v-model:value="form.rrule"
              type="text"
              :label="$t('calendar.rrule')"
              :placeholder="$t('calendar.rrulePlaceholder')"
            />

            <f7-list-item
              :title="$t('calendar.type')"
              smart-select
              :smart-select-params="{
                openIn: isSmallScreen? 'sheet': 'popover',
                closeOnSelect: true,
                sheetSwipeToClose: true,
              }"
            >
              <select v-model="form.type">
                <option v-for="t in eventTypes" :key="t.value" :value="t.value">
                  {{ t.labelKey }}
                </option>
              </select>
            </f7-list-item>

            <f7-list-item :title="$t('calendar.allDay')">
              <template #after>
                <f7-toggle v-model="form.allDay"></f7-toggle>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block-title>{{ $t("calendar.reminders") }}</f7-block-title>
          <f7-list
            media-list
            dividers
            v-for="(r, index) in form.reminders"
            :key="index"
            form
          >
            <f7-list-group>
              <f7-list-input
                v-model:value="r.minutesBefore"
                type="number"
                :label="$t('calendar.minutesBefore')"
                min="0"
                step="1"
              />
              <div class="grid cols80_20">
                <f7-list-input
                  :label="$t('calendar.method')"
                  name="method"
                  type="select"
                  v-model:value="r.method"
                >
                  <option selected value="DISPLAY" >
                    {{ $t("calendar.methodDisplay") }}
                  </option>
                  <option value="EMAIL">
                    {{ $t("calendar.methodEmail") }}
                  </option>
                </f7-list-input>
                <f7-button
                  small
                  round
                  outline
                  raised
                  color="red"
                  icon-ios="f7:minus_circle"
                  icon-md="material:do_not_disturb_on"
                  @click="removeReminder(index)"
                  >{{ $t("calendar.removeReminder") }}</f7-button
                >
              </div>
            </f7-list-group>
          </f7-list>

          <div class="poll-add-question">
            <f7-button
              @click="addReminder"
              ripple
              outline
              round raised
              large
              color="auto"
              icon-ios="f7:plus_circle"
              icon-md="material:add_circle"
              class="poll-add-question-btn"
            >
              {{ $t("calendar.addReminder") }}</f7-button
            >
          </div>
          <div
            :class="
              editingEvent
                ? 'grid grid-cols-2 grid-gap margin-top'
                : 'margin-top'
            "
          >
            <f7-button large fill round raised @click="saveEvent">
              {{
                editingEvent ? $t("calendar.save") : $t("calendar.createEvent")
              }}
            </f7-button>
            <f7-button
              v-if="editingEvent"
              large
              outline
              raised
              color="red"
              round
              class="margin-top"
              @click="confirmCancelEvent"
            >
              {{ $t("calendar.cancelEvent") }}
            </f7-button>
          </div>
        </f7-block>
      </f7-page>
    </AdaptiveSheet>

    <AdaptiveSheet class="cal-details-modal" :close-by-backdrop-click="false">
      <f7-page>
        <f7-navbar :title="$t('calendar.eventDetails')">
          <f7-nav-right>
            <f7-link sheet-close popup-close
              ><i class="icon icon-close"></i
            ></f7-link>
          </f7-nav-right>
        </f7-navbar>
        <f7-block>
          <div
            class="display-flex align-items-center gap-small margin-bottom-half"
          >
            <f7-badge :color="badgeColor(detailsEvent?.type)">{{
              typeLabel(detailsEvent?.type)
            }}</f7-badge>
            <f7-badge v-if="detailsEvent?.pinned" color="orange">
              <f7-icon f7="pin" size="12" /> {{ $t("calendar.pinnedLabel") }}
            </f7-badge>
          </div>
          <div class="detail-title">{{ detailsEvent?.title }}</div>

          <div class="detail-meta">
            <f7-icon f7="calendar" size="16" />
            {{ formatDateTime(detailsEvent?.startAt, detailsEvent?.allDay) }}
          </div>
          <div v-if="detailsEvent?.endAt" class="detail-meta">
            <f7-icon f7="clock" size="16" />
            {{
              $t("calendar.until", {
                date: formatDateTime(detailsEvent.endAt, detailsEvent.allDay),
              })
            }}
          </div>
          <div v-if="detailsEvent?.location" class="detail-meta">
            <f7-icon f7="map" size="16" /> {{ detailsEvent.location }}
          </div>
          <div v-if="detailsEvent?.description" class="detail-desc">
            {{ detailsEvent.description }}
          </div>

          <f7-block-title>{{ $t("calendar.responses") }}</f7-block-title>
          <f7-list>
            <f7-list-item :title="$t('calendar.willCome')" :after="detailsCounts.goingCount"/>
            <f7-list-item :title="$t('calendar.maybe')" :after="detailsCounts.maybeCount"/>
            <f7-list-item :title="$t('calendar.willNotCome')" :after="detailsCounts.declinedCount"/>
          </f7-list>

          <f7-block-title>{{ $t("calendar.yourResponse") }}</f7-block-title>
          <f7-block>
            <div class="rsvp-row">
              <f7-button
                small raised round
                :outline="detailsMyStatus !== 'GOING'"
                :fill="detailsMyStatus === 'GOING'"
                color="green"
                @click="setAttendance(detailsEvent, 'GOING')"
              >
                {{ $t("calendar.attending") }}
              </f7-button>
              <f7-button
                small raised
                round
                :outline="detailsMyStatus !== 'MAYBE'"
                :fill="detailsMyStatus === 'MAYBE'"
                color="orange"
                @click="setAttendance(detailsEvent, 'MAYBE')"
              >
                {{ $t("calendar.maybeAttend") }}
              </f7-button>
              <f7-button
                small raised
                round
                :outline="detailsMyStatus !== 'DECLINED'"
                :fill="detailsMyStatus === 'DECLINED'"
                color="red"
                @click="setAttendance(detailsEvent, 'DECLINED')"
              >
                {{ $t("calendar.notAttending") }}
              </f7-button>
            </div>
          </f7-block>

          <f7-block class="margin-top">
            <f7-button
              large
              outline raised round
              class="btn-blue"
              @click="downloadIcs(detailsEvent)"
            >
              {{ $t("calendar.addToMyCalendar") }}
            </f7-button>
            <f7-button
              v-if="isAdmin"
              large
              outline round raised
              color="orange"
              
              class="margin-top"
              @click="togglePin(detailsEvent)"
            >
              <f7-icon f7="pin" size="16" />
              {{
                detailsEvent?.pinned
                  ? $t("calendar.unpinEvent")
                  : $t("calendar.pinEvent")
              }}
            </f7-button>
            <f7-button
              v-if="isAdmin"
              large
              outline
              color="blue"
              round raised
              class="margin-top"
              @click="editFromDetails"
            >
              {{ $t("calendar.edit") }}
            </f7-button>
          </f7-block>
        </f7-block>
      </f7-page>
    </AdaptiveSheet>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AdaptiveSheet from "../common/AdaptiveSheet.vue";
import {
  openAdaptiveModal,
  closeAdaptiveModal,
} from "../../js/composables/useAdaptiveModal.js";
import dayjs from "../../js/day.js";
import i18n from "../../js/i18n.js";
import { useCalendarStore } from "../../js/pinia-stores/calendar.js";
import { useMembersStore } from "../../js/pinia-stores/members.js";
import { useKeycloakStore } from "../../js/pinia-stores/keycloak.js";
import {useSmallScreen} from "../../js/composables/useMediaQuery.js";

const { t } = i18n.global;

const props = defineProps({
  groupId: { type: String, required: true },
  initialEventId: { type: String, default: null },
  initialStart: { type: String, default: null },
});

const calendarStore = useCalendarStore();
const membersStore = useMembersStore();
const keycloakStore = useKeycloakStore();
const isSmallScreen = useSmallScreen().matches;
const eventTypes = [
  { value: "MEETING", labelKey: t("calendar.types.MEETING"), color: "blue" },
  { value: "DEADLINE", labelKey: t("calendar.types.DEADLINE"), color: "red" },
  {
    value: "CAMPAIGN",
    labelKey: t("calendar.types.CAMPAIGN"),
    color: "orange",
  },
  { value: "SOCIAL", labelKey: t("calendar.types.SOCIAL"), color: "green" },
  {
    value: "FUNDRAISING",
    labelKey: t("calendar.types.FUNDRAISING"),
    color: "pink",
  },
  {
    value: "TRAINING",
    labelKey: t("calendar.types.TRAINING"),
    color: "purple",
  },
  { value: "VOTE", labelKey: t("calendar.types.VOTE"), color: "teal" },
  {
    value: "REMINDER_ONLY",
    labelKey: t("calendar.types.REMINDER_ONLY"),
    color: "gray",
  },
];

const defaultReminders = [
  { minutesBefore: 1440, method: "DISPLAY" },
  { minutesBefore: 60, method: "DISPLAY" },
  { minutesBefore: 10, method: "DISPLAY" },
];

const viewDate = ref(dayjs().startOf("month"));
const selectedDate = ref(dayjs());
const loading = ref(false);

const editingEvent = ref(null);
const form = ref(emptyForm());
const detailsEvent = ref(null);

const isAdmin = computed(() => {
  const viewer = membersStore.members.find(
    (m) => m.userId === keycloakStore.subject,
  );
  return viewer?.role === "OWNER" || viewer?.role === "ADMIN";
});

const monthLabel = computed(() => viewDate.value.format("MMMM YYYY"));
const weekdays = computed(() => dayjs.weekdaysShort());
const selectedLabel = computed(() => selectedDate.value.format("D MMMM YYYY"));

const gridDays = computed(() => {
  const start = viewDate.value.startOf("week");
  const days = [];
  for (let i = 0; i < 42; i++) {
    days.push(start.add(i, "day"));
  }
  return days;
});

const eventsByDate = computed(() => {
  const map = {};
  for (const e of calendarStore.getEvents(props.groupId)) {
    if (e.status && e.status !== "ACTIVE") continue;
    const key = dayjs(e.startAt).format("YYYY-MM-DD");
    if (!map[key]) map[key] = [];
    map[key].push(e);
  }
  return map;
});

const selectedEvents = computed(() =>
  (eventsByDate.value[selectedDate.value.format("YYYY-MM-DD")] || [])
    .slice()
    .sort((a, b) => dayjs(a.startAt).valueOf() - dayjs(b.startAt).valueOf()),
);

const detailsCounts = computed(() => ({
  goingCount: detailsEvent.value?.summary?.goingCount ?? 0,
  maybeCount: detailsEvent.value?.summary?.maybeCount ?? 0,
  declinedCount: detailsEvent.value?.summary?.declinedCount ?? 0,
}));

const detailsMyStatus = computed(() => myStatus(detailsEvent.value));

function emptyForm() {
  return {
    id: null,
    title: "",
    description: "",
    startAt: "",
    endAt: "",
    location: "",
    type: "MEETING",
    allDay: false,
    rrule: "",
    reminders: defaultReminders.map((r) => ({ ...r })),
  };
}

function addReminder() {
  form.value.reminders.push({ minutesBefore: 60, method: "DISPLAY" });
}

function removeReminder(index) {
  form.value.reminders.splice(index, 1);
}
function isSameMonth(day) {
  return day.isSame(viewDate.value, "month");
}

function isToday(day) {
  return day.isSame(dayjs(), "day");
}

function isSelected(day) {
  return day.isSame(selectedDate.value, "day");
}

function hasEvents(day) {
  const list = eventsByDate.value[day.format("YYYY-MM-DD")];
  return !!list && list.length > 0;
}

function selectDay(day) {
  selectedDate.value = day;
}

function prevMonth() {
  viewDate.value = viewDate.value.subtract(1, "month");
  loadMonth();
}

function nextMonth() {
  viewDate.value = viewDate.value.add(1, "month");
  loadMonth();
}

function goToday() {
  viewDate.value = dayjs().startOf("month");
  selectedDate.value = dayjs();
  loadMonth();
}

async function loadMonth() {
  loading.value = true;
  try {
    const from = viewDate.value.startOf("month").format("YYYY-MM-DD");
    const to = viewDate.value.endOf("month").format("YYYY-MM-DD");
    const events = await calendarStore.fetchEvents(props.groupId, from, to);
    await Promise.allSettled(
      events
        .filter((e) => !e.summary)
        .map((e) => calendarStore.fetchAttendanceSummary(props.groupId, e.id)),
    );
  } catch (e) {
    f7.dialog.alert(t("calendar.errLoadEvents", { msg: e.message }));
  } finally {
    loading.value = false;
  }
}

async function loadMembers() {
  try {
    await membersStore.fetchMembers(props.groupId);
  } catch (e) {
    // members endpoint unavailable — admin tools stay hidden
  }
}

onMounted(async () => {
  await Promise.all([loadMonth(), loadMembers()]);
  if (props.initialEventId) await openInitialEvent();
});

function findEvent(id) {
  if (!id) return null;
  return (
    calendarStore.getEvents(props.groupId).find((e) => e.id === id) || null
  );
}

async function openInitialEvent() {
  let event = findEvent(props.initialEventId);
  if (!event && props.initialStart) {
    const d = dayjs(props.initialStart);
    if (d.isValid()) {
      viewDate.value = d.startOf("month");
      selectedDate.value = d;
      await loadMonth();
      event = findEvent(props.initialEventId);
    }
  }
  if (event) {
    selectedDate.value = dayjs(event.startAt);
    openEventDetails(event);
  }
}

async function togglePin(event) {
  if (!event) return;
  const pinned = !event.pinned;
  try {
    await calendarStore.setEventPinned(props.groupId, event.id, pinned);
    event.pinned = pinned;
    detailsEvent.value = { ...event };
    f7.toast.show({
      text: pinned ? t("calendar.pinned") : t("calendar.unpinned"),
      position: "bottom",
      closeTimeout: 1500,
    });
  } catch (e) {
    f7.dialog.alert(t("calendar.errPin", { msg: e.message }));
  }
}

function badgeColor(type) {
  const tpe = eventTypes.find((x) => x.value === type);
  return tpe ? tpe.color : "gray";
}

function typeLabel(type) {
  const meta = eventTypes.find((x) => x.value === type);
  return meta ? meta.labelKey : type;
}

function formatDateTime(iso, allDay) {
  if (!iso) return "";
  const d = dayjs(iso);
  return allDay ? d.format("DD/MM/YYYY") : d.format("DD/MM/YYYY HH:mm");
}

function myStatus(event) {
  if (!event) return null;
  if (event.myStatus) return event.myStatus;
  if (event.attendance?.status) return event.attendance.status;
  return null;
}

function openCreateEvent() {
  editingEvent.value = null;
  form.value = emptyForm();
  openAdaptiveModal(".cal-event-modal");
}

async function editEvent(event) {
  closeAdaptiveModal(".cal-details-modal");
  editingEvent.value = event;
  form.value = {
    id: event.id,
    title: event.title || "",
    description: event.description || "",
    startAt: dayjs(event.startAt).format("YYYY-MM-DDTHH:mm"),
    endAt: event.endAt ? dayjs(event.endAt).format("YYYY-MM-DDTHH:mm") : "",
    location: event.location || "",
    type: event.type || "MEETING",
    allDay: !!event.allDay,
    reminders: [],
  };
  openAdaptiveModal(".cal-event-modal");
  try {
    const reminders = await calendarStore.fetchEventReminders(
      props.groupId,
      event.id,
    );
    form.value.reminders = (
      reminders.length ? reminders : defaultReminders
    ).map((r) => ({
      minutesBefore: Math.max(1, Number(r.minutesBefore) || 1),
      method: r.method || "DISPLAY",
    }));
  } catch (e) {
    form.value.reminders = emptyForm().reminders;
  }
}

function editFromDetails() {
  editEvent(detailsEvent.value);
}

async function saveEvent() {
  if (!form.value.title || !form.value.startAt) {
    f7.dialog.alert(t("calendar.missingTitleStart"));
    return;
  }

  const payload = {
    title: form.value.title,
    description: form.value.description,
    location: form.value.location,
    type: form.value.type,
    allDay: !!form.value.allDay,
    reminders: (form.value.reminders || []).map((r) => ({
      minutesBefore: Math.max(1, Number(r.minutesBefore) || 1),
      method: r.method || "DISPLAY",
    })),
  };
  if (form.value.startAt) {
    payload.startAt = form.value.allDay
      ? dayjs(form.value.startAt).format("YYYY-MM-DD")
      : dayjs(form.value.startAt).toISOString();
  }
  if (form.value.endAt) {
    payload.endAt = form.value.allDay
      ? dayjs(form.value.endAt).format("YYYY-MM-DD")
      : dayjs(form.value.endAt).toISOString();
  }

  try {
    let event;
    if (editingEvent.value && form.value.id) {
      event = await calendarStore.updateEvent(
        props.groupId,
        form.value.id,
        payload,
      );
      f7.toast.show({
        text: t("calendar.updated"),
        position: "bottom",
        closeTimeout: 1500,
      });
    } else {
      event = await calendarStore.createEvent(props.groupId, payload);
      f7.toast.show({
        text: t("calendar.created"),
        position: "bottom",
        closeTimeout: 1500,
      });
    }

    closeAdaptiveModal(".cal-event-modal");
    if (event) {
      const d = dayjs(event.startAt);
      viewDate.value = d.startOf("month");
      selectedDate.value = d;
    }
    await loadMonth();
  } catch (e) {
    f7.dialog.alert(t("calendar.errSave", { msg: e.message }));
  }
}

function confirmCancelEvent() {
  f7.dialog.confirm(
    t("calendar.cancelConfirm"),
    t("calendar.cancelEventTitle"),
    async () => {
      try {
        await calendarStore.cancelEvent(props.groupId, form.value.id);
        f7.toast.show({
          text: t("calendar.cancelled"),
          position: "bottom",
          closeTimeout: 1500,
        });
        closeAdaptiveModal(".cal-event-modal");
        await loadMonth();
      } catch (e) {
        f7.dialog.alert(t("calendar.errCancel", { msg: e.message }));
      }
    },
  );
}

function openEventDetails(event) {
  detailsEvent.value = event;
  if (!event.summary) {
    calendarStore
      .fetchAttendanceSummary(props.groupId, event.id)
      .then((summary) => {
        detailsEvent.value = { ...event, summary };
      });
  }
  openAdaptiveModal(".cal-details-modal");
}

async function setAttendance(event, status) {
  try {
    await calendarStore.setAttendance(props.groupId, event.id, status);
    event.myStatus = status;
    if (detailsEvent.value?.id === event.id) {
      detailsEvent.value = { ...event };
    }
    f7.toast.show({
      text: t("calendar.responseSaved", { status: statusLabel(status) }),
      position: "bottom",
      closeTimeout: 1500,
    });
  } catch (e) {
    f7.dialog.alert(t("calendar.errResponse", { msg: e.message }));
  }
}

function statusLabel(status) {
  if (status === "GOING") return t("calendar.attending");
  if (status === "MAYBE") return t("calendar.maybeAttend");
  return t("calendar.notAttending");
}

async function downloadIcs(event) {
  try {
    await calendarStore.downloadIcs(props.groupId, event);
    f7.toast.show({
      text: t("calendar.icsDownloaded"),
      position: "bottom",
      closeTimeout: 1500,
    });
  } catch (e) {
    f7.dialog.alert(t("calendar.errDownload", { msg: e.message }));
  }
}
</script>

<style scoped>
/* Add question */
.poll-add-question {
  display: flex;
  justify-content: center;
  padding: 4px 0 8px;
}
.poll-add-question-btn {
  --f7-button-border-color: var(--avocash-green);
  --f7-button-text-color: var(--avocash-green);
  width: max-content;

  /* border: 2px solid var(--avocash-green); */
  /* border-radius: 25px; */
}

.calendar-view {
  --avo-green: var(--avocash-green, #1a6b42);
  --avo-green-light: var(--avocash-green-light, #eaf2eb);
  --avo-gold: var(--avocash-gold, #c9a84c);
  --avo-blue: var(--avocash-blue, #3b4fca);
  --avo-pit: var(--avocash-pit, #1c1c1e);
}

.cal-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  padding-top: 8px;
}
.cal-month {
  flex: 1;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  text-transform: capitalize;
}
.cal-today {
  font-size: 13px;
  color: var(--avo-blue);
}

.cal-grid-block {
  background: var(--f7-list-bg-color, #fff);
  border-radius: 12px;
  padding: 10px;
}
.cal-weekdays,
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}
.cal-weekday {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--f7-on-surface-variant, #8e8e93);
  padding: 4px 0;
}
.cal-cell {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 38px;
  border-radius: 8px;
  cursor: pointer;
}
.cal-day-num {
  font-size: 13px;
}
.cal-cell--other .cal-day-num {
  color: var(--f7-on-surface-variant, #8e8e93);
  opacity: 0.6;
}
.cal-cell--today .cal-day-num {
  color: var(--avo-green);
  font-weight: 700;
}
.cal-cell--selected {
  background: var(--avo-green-light);
}
.cal-cell--selected .cal-day-num {
  color: var(--avo-green);
  font-weight: 700;
}
.cal-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--avo-gold);
  margin-top: 2px;
}

.loading-block {
  text-align: center;
  padding: 20px 0;
}
.empty-text {
  font-size: 13px;
  color: var(--f7-on-surface-variant, #8e8e93);
  text-align: center;
  padding: 20px 0;
}

.cal-event-card {
  border-radius: 14px;
}
.cal-event-card :deep(.card-header) {
  font-size: 14px;
  gap: 8px;
}
.cal-event-card :deep(.card-footer) {
  padding: 10px 16px;
}
.cal-footer {
  flex-wrap: wrap;
  gap: 8px;
}
.text-right {
  text-align: right;
}
.margin-top-half {
  margin-top: 4px;
}
.margin-bottom-half {
  margin-bottom: 6px;
}
.margin-top {
  margin-top: 12px;
}
.gap-small {
  gap: 6px;
}

.cal-fab {
  z-index: 100;
}

.detail-title {
  font-size: 18px;
  font-weight: 600;
  margin: 8px 0 12px;
}
.detail-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--avo-pit);
  opacity: 0.75;
  margin-bottom: 6px;
}
.detail-desc {
  font-size: 14px;
  margin-top: 10px;
}
.rsvp-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.btn-green {
  background: var(--avo-green);
  color: #fff;
}
.btn-blue {
  background: var(--avo-blue);
  color: #fff;
}
.btn-green.active-state,
.btn-blue.active-state {
  opacity: 0.8;
}
</style>
